import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Grid, List, Table } from 'lucide-react';
import { useModels, useLinkDomains } from '../hooks';
import { ModelCard } from './ModelCard';
import { ModelTableRow } from './ModelTableRow';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Pagination } from '@/components/Pagination';
import { useUIStore } from '@/stores/ui';
import { useViewMode } from '@/features/preferences';

// Helper to create display names from domains
function formatDomainLabel(domain: string): string {
  // Map known domains to friendly names
  const domainNames: Record<string, string> = {
    'makerworld.com': 'MakerWorld',
    'printables.com': 'Printables',
    'thingiverse.com': 'Thingiverse',
    'thangs.com': 'Thangs',
    'cults3d.com': 'Cults3D',
    'myminifactory.com': 'MyMiniFactory',
  };

  return domainNames[domain] || domain;
}

export function ModelLibraryBrowser() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setSelectedCollectionIds, collectionsFilterOpen, toggleCollectionsFilter } = useUIStore();

  // View mode with persistence
  const { viewMode, setViewMode } = useViewMode('models');

  // Fetch unique link domains for filter dropdown
  const { data: linkDomains = [] } = useLinkDomains();

  // Read all filters from URL
  const search = searchParams.get('search') || '';
  const sourceType = searchParams.get('sourceType') || '';
  const sortBy = (searchParams.get('sortBy') as 'name' | 'createdAt' | 'addedAt') || 'addedAt';
  const page = parseInt(searchParams.get('page') || '1', 10);

  // Read collection filters from URL
  const collectionIds = searchParams.getAll('collection');

  // Sync URL collections to UI store (for sidebar highlighting)
  useEffect(() => {
    setSelectedCollectionIds(collectionIds);
  }, [collectionIds.join(','), setSelectedCollectionIds]);

  // Parse smart collections from URL
  const smartCollections = collectionIds.filter((id) => id.startsWith('recent-') || ['favorites', 'uncategorized'].includes(id));
  const regularCollections = collectionIds.filter((id) => !id.startsWith('recent-') && !['favorites', 'uncategorized'].includes(id));

  // Helper to update a single filter in URL
  const updateFilter = (key: string, value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    // Reset to page 1 when filters change (except for page itself)
    if (key !== 'page') {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  // Build query based on smart collections
  let queryParams: any = {
    search: search || undefined,
    sourceType: sourceType || undefined,
    sortBy,
    sortOrder: 'desc',
    page,
    limit: 20,
  };

  // Handle smart collections
  if (smartCollections.includes('favorites')) {
    queryParams.isFavorite = true;
  }
  if (smartCollections.includes('uncategorized')) {
    queryParams.uncollected = true;
  }
  // Handle Recent sub-collections
  if (smartCollections.includes('recent-viewed')) {
    queryParams.recentViewed = true;
    queryParams.recentDays = 7;
  }
  if (smartCollections.includes('recent-added')) {
    queryParams.recentAdded = true;
    queryParams.recentDays = 7;
  }
  if (smartCollections.includes('recent-printed')) {
    queryParams.recentPrinted = true;
    queryParams.recentDays = 7;
  }

  // Handle regular collections
  if (regularCollections.length > 0) {
    queryParams.collectionIds = regularCollections;
  }

  const { data, isLoading, isError } = useModels(queryParams);

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        {/* Toggle Collections Filter Button */}
        <Button
          size="sm"
          variant="secondary"
          onClick={toggleCollectionsFilter}
          className="flex-shrink-0"
        >
          {collectionsFilterOpen ? 'Hide' : 'Show'} Collections
        </Button>

        {/* Search */}
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Search models..."
            value={search}
            onChange={(e) => updateFilter('search', e.target.value || null)}
            leftIcon={<Search className="h-5 w-5" />}
          />
        </div>

        {/* Source Type Filter */}
        <div className="w-full sm:w-48">
          <Select
            value={sourceType}
            onChange={(e) => updateFilter('sourceType', e.target.value || null)}
            options={[
              { value: '', label: 'All Sources' },
              ...linkDomains.map((domain) => ({
                value: domain,
                label: formatDomainLabel(domain),
              })),
            ]}
          />
        </div>

        {/* Sort */}
        <div className="w-full sm:w-52">
          <Select
            value={sortBy}
            onChange={(e) => updateFilter('sortBy', e.target.value)}
            options={[
              { value: 'addedAt', label: 'Sort by Date Added' },
              { value: 'name', label: 'Sort by Name' },
              { value: 'createdAt', label: 'Sort by Date Created' },
            ]}
          />
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-1 p-1 bg-background-subtle rounded-lg flex-shrink-0">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded ${
              viewMode === 'grid'
                ? 'bg-background text-foreground'
                : 'text-foreground-muted'
            }`}
            title="Grid view"
          >
            <Grid className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded ${
              viewMode === 'list'
                ? 'bg-background text-foreground'
                : 'text-foreground-muted'
            }`}
            title="List view"
          >
            <List className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded ${
              viewMode === 'table'
                ? 'bg-background text-foreground'
                : 'text-foreground-muted'
            }`}
            title="Table view"
          >
            <Table className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-foreground-muted">Loading models...</p>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="text-center py-12">
          <p className="text-error">Failed to load models</p>
        </div>
      )}

      {/* Results */}
      {data && (
        <>
          {/* Grid/List/Table View */}
          {data.items.length > 0 ? (
            <>
              {viewMode === 'table' ? (
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-background-subtle">
                      <tr className="border-b border-border">
                        <th className="p-3 text-left text-xs font-semibold text-foreground-muted uppercase tracking-wide">
                          Thumbnail
                        </th>
                        <th className="p-3 text-left text-xs font-semibold text-foreground-muted uppercase tracking-wide">
                          Name
                        </th>
                        <th className="p-3 text-left text-xs font-semibold text-foreground-muted uppercase tracking-wide">
                          Files
                        </th>
                        <th className="p-3 text-left text-xs font-semibold text-foreground-muted uppercase tracking-wide">
                          Links
                        </th>
                        <th className="p-3 text-right text-xs font-semibold text-foreground-muted uppercase tracking-wide">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((model) => (
                        <ModelTableRow key={model.id} model={model} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                      : 'space-y-4'
                  }
                >
                  {data.items.map((model) => (
                    <ModelCard key={model.id} model={model} variant={viewMode === 'list' ? 'compact' : 'default'} />
                  ))}
                </div>
              )}

              {/* Pagination */}
              <Pagination
                page={page}
                total={data.pagination.total}
                limit={data.pagination.limit}
                onPageChange={(newPage) => updateFilter('page', String(newPage))}
              />
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-foreground-muted">No models found</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
